import { generateInvoicesAndTransactionsForReconciliation } from "@/apis/developer-tools";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

type Props = {
  isDialogOpen: boolean;
  setIsDialogOpen: (isDialogOpen: boolean) => void;
};

export const TriggerReconciledTransactions = ({
  isDialogOpen,
  setIsDialogOpen,
}: Props) => {
  const [invoiceCount, setInvoiceCount] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirmAction = async () => {
    try {
      setIsLoading(true);
      await generateInvoicesAndTransactionsForReconciliation(invoiceCount);
      setIsDialogOpen(false);
    } catch (error) {
      setError(error as string);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setError(null);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
      <DialogPortal container={document.body}>
        <DialogContent
          className="z-[9999]" // Ensure it's above everything
          onPointerDownOutside={(e) => {
            e.preventDefault();
            handleDialogClose(false);
          }}
          onEscapeKeyDown={() => {
            handleDialogClose(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>Generate Test Transactions & Invoices</DialogTitle>
            <DialogDescription>
              This will randomly generate fake bank transactions and create
              invoices based on those transactions.
            </DialogDescription>
          </DialogHeader>

          <Alert variant="warning">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Important Prerequisite</AlertTitle>
            <AlertDescription>
              Ensure the SMB has a bank account connected before proceeding.
            </AlertDescription>
          </Alert>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                There was an error triggering the process. Please try again.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 py-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="invoice-count">Number of Random Invoices</Label>
              <Input
                id="invoice-count"
                type="number"
                min="1"
                max="1000"
                autoFocus={false}
                value={invoiceCount}
                onChange={(e) => setInvoiceCount(Number(e.target.value))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleDialogClose(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmAction} disabled={isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Generate Test Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
